import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModalConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'services/toast.service';
import { BaseFormModalComponent } from '../base-form-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ApiService } from 'services/api.service';
import { NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import {
  FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE
} from 'types/information-request.interface';
import type {
  BackofficeInformationRequestCard,
  BackofficeInformationRequestCommunicationCard,
  BackofficeInformationRequestCommunicationListItem,
  InformationRequestBackofficeStatus,
  InformationRequestCard,
  LogAdditionalInformationForInformationRequestRequest,
  UpdateLogAdditionalInformationForInformationRequestRequest
} from 'types/information-request.interface';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';

@Component({
  selector: 'app-ir-log-info-modal',
  standalone: true,
  imports: [TranslateModule, SpinnerComponent, NgClass, TooltipComponent, ReactiveFormsModule, FileUploadComponent],
  templateUrl: './ir-log-info-modal.component.html',
  styleUrls: ['./ir-log-info-modal.component.scss', '../../form/base-form.scss']
})
export class IrLogInfoModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Output() update: EventEmitter<BackofficeInformationRequestCommunicationCard | BackofficeInformationRequestCommunicationListItem> =
    new EventEmitter<BackofficeInformationRequestCommunicationCard | BackofficeInformationRequestCommunicationListItem>();
  @Output() requestRefreshed: EventEmitter<InformationRequestCard> = new EventEmitter<InformationRequestCard>();
  @ViewChild('content') content!: TemplateRef<any>;
  public readonly communicationFileType = FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE;
  @Input() note: BackofficeInformationRequestCommunicationCard | BackofficeInformationRequestCommunicationListItem | null = null;
  shouldRenderTooltip = false;
  // Live backoffice status fetched when the modal opens. The "need to process" checkbox is
  // gated on this (not the stale @Input) so a non-reloaded page stops offering it once the
  // client has already answered. null until the fetch resolves → checkbox stays hidden.
  liveBackofficeStatus: InformationRequestBackofficeStatus | null = null;

  isCrudLoading = false;
  private pendingResponse: BackofficeInformationRequestCommunicationCard | BackofficeInformationRequestCommunicationListItem | null = null;
  get backofficeText(): AbstractControl<any, any> | null {
    return this.form.get('backofficeText');
  }
  isSubmittingFiles = false;
  override fileUploadComponentCount = 1;

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.refreshLiveStatus();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.refreshLiveStatus();
      this.shouldRenderTooltip = false;
      this.open(this.content);

      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  /**
   * Re-fetch the information request so the checkbox gate reflects the real current status,
   * not the (possibly stale) value the page passed down. Also bubbles the fresh request up so
   * the page's status badge updates in place. Kept null-safe — on failure the checkbox simply
   * stays hidden, which is the safe default.
   */
  private refreshLiveStatus(): void {
    this.liveBackofficeStatus = null;
    const id = this.informationRequest?.id;
    if (!id) {
      return;
    }
    this.apiService.getInformationRequest(id).subscribe({
      next: (data) => {
        this.liveBackofficeStatus = data.backofficeStatus;
        this.requestRefreshed.emit(data);
      },
      error: (err) => console.error(err)
    });
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      backofficeText: [this.note?.backofficeText || '', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]],
      needToProcess: [false]
    });
  }

  protected override close(): void {
    super.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      if (!this.note) {
        const args: LogAdditionalInformationForInformationRequestRequest = {
          informationRequestId: this.informationRequest.id,
          backofficeText: this.form.value.backofficeText,
          needToProcess: this.form.value.needToProcess
        };

        this.apiService.createIrLogInfo(args).subscribe({
          next: (response) => {
            this.toastService.showSuccess('createAppClientNoteSuccess', '', {
              shouldTranslate: true
            });
            this.pendingResponse = response;
            this.note = response;
            this.isSubmittingFiles = true;
          },
          error: (err) => {
            // BE rejects needToProcess when the request is no longer awaiting the client.
            // Surface the friendly "status already changed" message (via extractKnownError) and
            // re-sync the live status / page badge instead of the raw "Sistēmas kļūda".
            this.toastService.showRequestError(err);
            this.isCrudLoading = false;
            this.refreshLiveStatus();
            this.close();
          }
        });
      } else {
        const args: UpdateLogAdditionalInformationForInformationRequestRequest = {
          informationRequestCommunicationId: this.note.id,
          backofficeText: this.form.value.backofficeText
        };

        this.apiService.updateIrLogInfo(args).subscribe({
          next: (response) => {
            this.toastService.showSuccess('updateAppClientNoteSuccess', '', {
              shouldTranslate: true
            });
            this.pendingResponse = response;
            this.isSubmittingFiles = true;
          },
          error: () => {
            this.toastService.showRequestError();
            this.isCrudLoading = false;
            this.close();
          }
        });
      }
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.addAppClientNote');
    }
  }

  protected override onSubmitSuccess(): void {
    this.isSubmittingFiles = false;
    this.fileActions = [];
    this.uploadComponentsReceived = 0;
    this.note = this.pendingResponse;
    this.close();

    if (this.note) {
      this.update.emit(this.note);
    }
    this.pendingResponse = null;
  }

  get shouldRenderNeedToChangeStatusButton(): boolean {
    return this.liveBackofficeStatus === 'AdditionalInfoRequested';
  }

  getFiles(note: BackofficeInformationRequestCommunicationCard | BackofficeInformationRequestCommunicationListItem | null) {
    if (note && 'backofficeFiles' in note) {
      return note.backofficeFiles;
    }

    return [];
  }
}
